import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Label } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { INDUSTRIES } from "../../lib/industries";
import { Upload } from "lucide-react";
import { TimezoneSelect } from "../../components/TimezoneSelect";
import { DEFAULT_TZ } from "../../lib/time";

export default function Settings() {
  const [org, setOrg] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/organization").then((r) => { setOrg(r.data.organization); setSettings(r.data.settings); }).catch(() => {});
  }, []);

  const saveOrg = async () => {
    try { await api.patch("/organization", { name: org.name, industry: org.industry, logo_url: org.logo_url, google_review_url: org.google_review_url, negative_alerts_enabled: org.negative_alerts_enabled !== false, timezone: org.timezone || DEFAULT_TZ }); toast.success("Organisation updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 1024 * 1024) { toast.error("Logo must be under 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setOrg((o) => ({ ...o, logo_url: reader.result }));
    reader.onerror = () => toast.error("Could not read that image.");
    reader.readAsDataURL(file);
  };
  const saveSettings = async () => {
    try {
      await api.patch("/organization/settings", {
        min_employee_reviews: Number(settings.min_employee_reviews),
        min_location_reviews: Number(settings.min_location_reviews),
        confidence_k: Number(settings.confidence_k),
        recency_half_life_days: Number(settings.recency_half_life_days),
      });
      toast.success("Scoring configuration updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const WEIGHT_KEYS = ["weight_rating", "weight_touchpoints", "weight_questions", "weight_dimensions"];
  const WEIGHT_LABELS = {
    weight_rating: "Star rating (1–5)",
    weight_touchpoints: "Standard touchpoints (yes/no)",
    weight_questions: "Custom rating questions",
    weight_dimensions: "Touchpoint dimensions (1–5)",
  };
  const WEIGHT_HINTS = {
    weight_rating: "The headline stars the customer taps first.",
    weight_touchpoints: "Felt-Valued · Understood · Communication · Resolution.",
    weight_questions: "Your organisation's custom yes/no questions.",
    weight_dimensions: "Fine-grained sliders like Friendliness, Speed, Accuracy.",
  };
  const WEIGHT_DEFAULTS = { weight_rating: 0.5, weight_touchpoints: 0.2, weight_questions: 0.2, weight_dimensions: 0.1 };

  const weightTotal = WEIGHT_KEYS.reduce((s, k) => s + Number(settings?.[k] ?? WEIGHT_DEFAULTS[k]), 0);
  const normPct = (k) => {
    const v = Number(settings?.[k] ?? WEIGHT_DEFAULTS[k]);
    return weightTotal > 0 ? Math.round((v / weightTotal) * 100) : 0;
  };

  const setWeight = (k, v) => setSettings((s) => ({ ...s, [k]: Number(v) }));
  const resetWeights = () => setSettings((s) => ({ ...s, ...WEIGHT_DEFAULTS, no_comment_discount: 0.10 }));

  const noCommentDiscount = Number(settings?.no_comment_discount ?? 0.10);
  const previewSSI = (() => {
    // What a 5★ no-comment feedback would score under the current weights (rating channel only).
    const base = 1.0; // perfect rating normalised
    const adj = base * (1 - noCommentDiscount) + 0.60 * noCommentDiscount;
    return (adj * 5).toFixed(2);
  })();

  const saveWeights = async () => {
    try {
      const body = Object.fromEntries(WEIGHT_KEYS.map((k) => [k, Number(settings[k] ?? WEIGHT_DEFAULTS[k])]));
      body.no_comment_discount = Number(noCommentDiscount);
      await api.patch("/organization/settings", body);
      toast.success("Response weighting updated — new feedback will use these weights immediately");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!org || !settings) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6">
      <div><Label>Configuration</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Settings</h1></div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <Label>Organisation & Branding</Label>
          <div className="mt-4 space-y-4">
            <Fld label="Organisation name"><Input value={org.name || ""} onChange={(e) => setOrg({ ...org, name: e.target.value })} data-testid="org-name" /></Fld>
            <Fld label="Industry">
              <Select value={org.industry || ""} onValueChange={(v) => setOrg({ ...org, industry: v })}>
                <SelectTrigger data-testid="org-industry"><SelectValue placeholder="Select your industry" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {INDUSTRIES.map((i) => <SelectItem key={i} value={i} data-testid={`industry-opt-${i}`}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-400">Used to benchmark you against peers in the same industry.</p>
            </Fld>
            <Fld label="Logo">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  {org.logo_url ? (
                    <img src={org.logo_url} alt="Logo preview" className="h-full w-full object-contain" data-testid="logo-preview" />
                  ) : (
                    <span className="text-[10px] font-semibold uppercase text-slate-400">No logo</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="logo-upload-label">
                    <Upload className="h-4 w-4" /> Upload logo
                    <input type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="logo-upload-input" />
                  </label>
                  {org.logo_url && (
                    <button type="button" onClick={() => setOrg({ ...org, logo_url: "" })} className="ml-2 text-sm font-semibold text-red-600 hover:underline" data-testid="logo-remove">Remove</button>
                  )}
                  <p className="text-xs text-slate-400">PNG, JPG or SVG · under 1 MB. Shown on the customer feedback screen.</p>
                </div>
              </div>
            </Fld>
            <Fld label="Or paste a logo URL"><Input value={typeof org.logo_url === "string" && !org.logo_url.startsWith("data:") ? org.logo_url : ""} onChange={(e) => setOrg({ ...org, logo_url: e.target.value })} placeholder="https://…" data-testid="org-logo-url" /></Fld>
            <Fld label="Google review link (positive flow)"><Input value={org.google_review_url || ""} onChange={(e) => setOrg({ ...org, google_review_url: e.target.value })} data-testid="org-google-review" /></Fld>
            <Fld label="Default time zone (used where no location applies)"><TimezoneSelect value={org.timezone || DEFAULT_TZ} onChange={(tz) => setOrg({ ...org, timezone: tz })} testId="org-timezone" /></Fld>
            <Button onClick={saveOrg} data-testid="save-org" className="bg-[#4338CA] hover:bg-[#3730A3]">Save branding</Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <Label>Scoring & Confidence</Label>
          <p className="mt-1 text-sm text-slate-500">Control how the Servantage Service Index handles sample size and recency.</p>
          <div className="mt-4 space-y-4">
            <Fld label="Minimum employee reviews"><Input type="number" value={settings.min_employee_reviews} onChange={(e) => setSettings({ ...settings, min_employee_reviews: e.target.value })} data-testid="min-emp-reviews" /></Fld>
            <Fld label="Minimum location reviews"><Input type="number" value={settings.min_location_reviews} onChange={(e) => setSettings({ ...settings, min_location_reviews: e.target.value })} data-testid="min-loc-reviews" /></Fld>
            <Fld label="Confidence smoothing (K)"><Input type="number" value={settings.confidence_k} onChange={(e) => setSettings({ ...settings, confidence_k: e.target.value })} /></Fld>
            <Fld label="Recency half-life (days)"><Input type="number" value={settings.recency_half_life_days} onChange={(e) => setSettings({ ...settings, recency_half_life_days: e.target.value })} /></Fld>
            <Button onClick={saveSettings} data-testid="save-settings" className="bg-[#4338CA] hover:bg-[#3730A3]">Save scoring</Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="response-weighting-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Label>Response Weighting</Label>
              <p className="mt-1 text-sm text-slate-500">Decide how much each part of a feedback submission contributes to the Service Index. Applies globally to every location in your organisation.</p>
            </div>
            <button type="button" onClick={resetWeights} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="weights-reset">Reset to defaults</button>
          </div>

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {WEIGHT_KEYS.map((k) => (
              <div key={k} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{WEIGHT_LABELS[k]}</span>
                  <span className="font-head font-bold text-indigo-700" data-testid={`weight-${k}-pct`}>{normPct(k)}%</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{WEIGHT_HINTS[k]}</p>
                <input type="range" min="0" max="1" step="0.05"
                  value={Number(settings[k] ?? WEIGHT_DEFAULTS[k])}
                  onChange={(e) => setWeight(k, e.target.value)}
                  data-testid={`weight-${k}`}
                  className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-[#4338CA]" />
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-800">No-comment penalty</span>
              <span className="font-head font-bold text-amber-700" data-testid="no-comment-pct">{Math.round(noCommentDiscount * 100)}%</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">Pull a feedback with no written comment this fraction of the way toward 3-star. Higher = stronger discount on silent 5-stars. 0 switches it off.</p>
            <input type="range" min="0" max="0.3" step="0.01" value={noCommentDiscount}
              onChange={(e) => setSettings({ ...settings, no_comment_discount: Number(e.target.value) })}
              data-testid="weight-no_comment_discount"
              className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-amber-200 accent-amber-600" />
            <p className="mt-3 text-xs text-slate-500" data-testid="weight-preview">
              Preview: a 5★ with no comment currently contributes <strong className="text-slate-800">{previewSSI} / 5</strong> to the weighted quality.
            </p>
          </div>

          <Button onClick={saveWeights} data-testid="save-weights" className="mt-5 bg-[#4338CA] hover:bg-[#3730A3]">Save weighting</Button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <Label>Notifications</Label>
          <p className="mt-1 text-sm text-slate-500">Alert your managers the moment something needs attention.</p>
          <label className="mt-4 flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 p-4" data-testid="negative-alerts-row">
            <span>
              <span className="block font-semibold text-slate-800">Instant negative-feedback emails</span>
              <span className="block text-sm text-slate-500">Email location & corporate admins when a customer leaves a low rating ({"≤2★"}) or opens a service-recovery case.</span>
            </span>
            <button type="button" role="switch" aria-checked={org.negative_alerts_enabled !== false}
              onClick={() => setOrg({ ...org, negative_alerts_enabled: org.negative_alerts_enabled === false })}
              data-testid="toggle-negative-alerts"
              className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${org.negative_alerts_enabled !== false ? "bg-[#4338CA]" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${org.negative_alerts_enabled !== false ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </label>
          <Button onClick={saveOrg} data-testid="save-notifications" className="mt-4 bg-[#4338CA] hover:bg-[#3730A3]">Save notifications</Button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">{label}</Label>{children}</div>;
}
